<template>
    <Card class="sales-stat-card">
        <Row class="filter-bar" :gutter="12">
            <Col class="title">📊 销售统计图表</Col>

            <!-- 选择年份 -->
            <Col class="filter-item">
                <DatePicker
                    v-model="year"
                    transfer
                    type="year"
                    placeholder="选择年"
                    format="yyyy"
                    value-format="yyyy"
                    @on-change="handleSearch"
                ></DatePicker>
            </Col>

            <!-- 分组维度 -->
            <Col class="filter-item" v-if="this.$checkAccessArray(['S', 'I'])">
                <span class="label">分组维度: </span>
                <RadioGroup
                    v-model="groupBy"
                    type="button"
                    size="small"
                    @on-change="chooseGroup"
                >
                    <Radio
                        v-for="o in GROUP_BY_OPTS"
                        :key="o.value"
                        :label="o.value"
                    >
                        {{ o.label }}
                    </Radio>
                </RadioGroup>
            </Col>

            <!-- 苗场选择 -->
            <Col v-show="seedFarmListShow">
                <span class="label">苗场: </span>
                <Select
                    v-model="seedFarmUuid"
                    transfer
                    filterable
                    clearable
                    style="width: 200px"
                    @on-change="handleSearch"
                >
                    <Option
                        v-for="(item,index) in seedFarmList"
                        :value="item.seedFarmUuid"
                        :key="index"
                        >{{ item.seedFarmCode }}---{{ item.seedFarmName }}</Option
                    >
                </Select>
            </Col>

            <Col v-show="managerListShow">
                <span class="label">客户经理: </span>
                <Select
                    v-model="managerUuid"
                    transfer
                    filterable
                    clearable
                    style="width: 200px"
                    @on-change="handleSearch"
                >
                    <Option
                        v-for="(item,index) in managerList"
                        :value="item.userCustomerManagerLoginUuid"
                        :key="index"
                        >{{ item.userCustomerManagerCode }}---{{ item.userCustomerManagerName }}</Option
                    >
                </Select>
            </Col>

            <Col v-show="distributionStationListShow">
                <span class="label">转运站: </span>
                <Select
                    v-model="distributionStationUuid"
                    transfer
                    filterable
                    clearable
                    style="width: 200px"
                    @on-change="handleSearch"
                >
                    <Option
                        v-for="(item,index) in distributionStationList"
                        :value="item.distributionStationUuid"
                        :key="index"
                        >{{ item.distributionStationCode }}---{{ item.distributionStationName }}</Option>
                </Select>
            </Col>

            <!-- 搜索按钮 -->
            <Col>
                <Button
                    class="search-btn"
                    size="small"
                    type="primary"
                    shape="circle"
                    icon="ios-search"
                    @click="handleSearch"
                    >搜索</Button
                >
            </Col>
        </Row>

        <!-- 图表区域 -->
        <Spin :spinning="loading || !dataLoaded" :tip="loading ? '数据加载中...' : '等待数据...'">
            <section class="chart-wrapper">
                <div ref="barChartRef" style="width: 100%; height: 400px" v-show="chartsInitialized && dataLoaded"></div>
                <div v-if="!chartsInitialized || !dataLoaded" style="width: 100%; height: 400px; display: flex; align-items: center; justify-content: center; background: #f5f5f5; border-radius: 8px;">
                    <div style="text-align: center;">
                        <div style="margin-bottom: 12px;">
                            <Icon type="ios-loading" size="24" class="spin-icon-load"></Icon>
                        </div>
                        <span style="color: #666;">{{ loading ? '图表初始化中...' : '等待数据加载...' }}</span>
                    </div>
                </div>
            </section>
            <section class="chart-wrapper">
                <div
                    ref="lineChartRef"
                    style="width: 100%; height: 400px"
                    v-show="chartsInitialized && dataLoaded"
                ></div>
                <div v-if="!chartsInitialized || !dataLoaded" style="width: 100%; height: 400px; display: flex; align-items: center; justify-content: center; background: #f5f5f5; border-radius: 8px;">
                    <div style="text-align: center;">
                        <div style="margin-bottom: 12px;">
                            <Icon type="ios-loading" size="24" class="spin-icon-load"></Icon>
                        </div>
                        <span style="color: #666;">{{ loading ? '图表初始化中...' : '等待数据加载...' }}</span>
                    </div>
                </div>
            </section>
        </Spin>
    </Card>
</template>

<script>
import search from "@/libs/search";
import * as echarts from "echarts";
import { getSalesStatistical } from "@/api/modules/sale-management/sale-stat";
import { getCustomerManagerUserList } from "@/api/modules/account-management/customerManager-user-management";
import { getSeedFarmList } from "@/api/modules/system-management/seedFarm-management";
import { getDistributionStationList } from "@/api/modules/system-management/distributionStation-management";
import { debounce } from "lodash-es";

export default {
    name: "SalesStatisticalCharts",
    data() {
        return {
            GROUP_BY_OPTS: [
                { label: "所有", value: "all" },
                { label: "苗场", value: "seedFarm" },
                { label: "转运站", value: "distributionStation" },
                { label: "客户经理", value: "manager" }
            ],
            groupBy: "all",

            currentYearData: [],
            lastYearData: [],
            loading: false,
            dataCache: new Map(),

            barChart: null,
            lineChart: null,
            __resize: null,
            chartOptions: {
                bar: null,
                line: null
            },
            chartsInitialized: false,
            dataLoaded: false,

            managerListShow: false,
            managerUuid: "",
            managerList: [],

            seedFarmListShow: false,
            seedFarmUuid: "",
            seedFarmList: [],

            distributionStationListShow: false,
            distributionStationUuid: "",
            distributionStationList: [],

            year: String(new Date().getFullYear())

        };
    },
    mounted() {
        this.initCharts();
        this.fetchData();
        this.getCustomerManagerUserTableRecords();
        this.getSeedFarmList();
        this.getDistributionStationList();
        this.__resize = debounce(() => {
            if (this.barChart) this.barChart.resize();
            if (this.lineChart) this.lineChart.resize();
        }, 300);
        window.addEventListener("resize", this.__resize);
    },
    beforeDestroy() {
        window.removeEventListener("resize", this.__resize);
        if (this.barChart && this.barChart.dispose) {
            this.barChart.dispose();
            this.barChart = null;
        }
        if (this.lineChart && this.lineChart.dispose) {
            this.lineChart.dispose();
            this.lineChart = null;
        }
    },
    methods: {
        initCharts() {
            if (this.chartsInitialized) return;
            
            this.$nextTick(() => {
                if (this.$refs.barChartRef && this.$refs.lineChartRef) {
                    setTimeout(() => {
                        if (this.$refs.barChartRef && this.$refs.lineChartRef) {
                            this.barChart = echarts.init(this.$refs.barChartRef);
                            this.lineChart = echarts.init(this.$refs.lineChartRef);
                            this.chartsInitialized = true;
                            this.initChartOptions();
                        }
                    }, 100);
                }
            });
        },

        formatNumber(value) {
            if (value === null || value === undefined) return '0';
            const num = Number(value);
            if (num < 10000) {
                return num.toLocaleString();
            }
            else {
                const wan = (num / 10000).toFixed(1);
                const formattedWan = parseFloat(wan).toLocaleString();
                return wan.endsWith('.0') ? formattedWan + '万' : formattedWan + '万';
            }
        },

        formatNumberWithDetail(value) {
            if (value === null || value === undefined) return '0';
            const num = Number(value);
            if (num < 10000) {
                return num.toString();
            }
            else {
                const wan = (num / 10000).toFixed(1);
                const formattedWan = wan.endsWith('.0') ? wan.slice(0, -2) + '万' : wan + '万';
                const formattedNum = num.toLocaleString();
                return `${formattedWan} (${formattedNum})`;
            }
        },

        initChartOptions() {
            if (!this.barChart || !this.lineChart) return;
            
            const currYear = this.year.getFullYear();
            const prevYear = currYear - 1;
            const xAxisData = Array.from({ length: 12 }, (_, i) => `${i + 1} 月`);

            this.chartOptions.bar = {
                color: ['red', 'blue'],
                title: {
                    text: `${currYear} 与 ${prevYear} 实际发货量对比`,
                    left: "center"
                },
                tooltip: {
                    trigger: "axis",
                    axisPointer: { type: "shadow" },
                    formatter: (params) => {
                        let result = params[0].name + '<br/>';
                        params.forEach(param => {
                            const value = param.value || 0;
                            const detail = this.formatNumberWithDetail(value);
                            result += `${param.marker}${param.seriesName}: ${detail}<br/>`;
                        });
                        return result;
                    }
                },
                legend: { 
                    top: 30,
                    selectedMode: true
                },
                grid: {
                    left: 60,
                    right: 60,
                    bottom: 40,
                    top: 70,
                    containLabel: true
                },
                xAxis: { 
                    type: "category", 
                    data: xAxisData
                },
                yAxis: { 
                    type: "value", 
                    name: "数量"
                },
                series: [
                    {
                        name: currYear.toString(),
                        type: "bar",
                        data: Array(12).fill(0),
                        label: {
                            show: true,
                            position: "top",
                            formatter: (params) => this.formatNumber(params.value)
                        }
                    },
                    {
                        name: prevYear.toString(),
                        type: "bar",
                        data: Array(12).fill(0),
                        label: {
                            show: true,
                            position: "top",
                            formatter: (params) => this.formatNumber(params.value)
                        }
                    }
                ]
            };

            this.chartOptions.line = {
                color: ['red', 'blue'],
                title: {
                    text: `${currYear} 与 ${prevYear} 有效金额对比`,
                    left: "center"
                },
                tooltip: { 
                    trigger: "axis", 
                    axisPointer: { type: "line" },
                    formatter: (params) => {
                        let result = params[0].name + '<br/>';
                        params.forEach(param => {
                            const value = param.value || 0;
                            const detail = this.formatNumberWithDetail(value);
                            result += `${param.marker}${param.seriesName}: ${detail}元<br/>`;
                        });
                        return result;
                    }
                },
                legend: { 
                    top: 30,
                    selectedMode: true
                },
                grid: {
                    left: 60,
                    right: 60,
                    bottom: 40,
                    top: 70,
                    containLabel: true
                },
                xAxis: { 
                    type: "category", 
                    data: xAxisData
                },
                yAxis: {
                    type: "value",
                    name: "金额 (元)"
                },
                series: [
                    {
                        name: currYear.toString(),
                        type: "line",
                        smooth: true,
                        data: Array(12).fill(0),
                        label: {
                            show: true,
                            position: "top",
                            formatter: (params) => this.formatNumber(params.value),
                            distance: 10,
                            avoidLabelOverlap: true
                        },
                        labelLayout: {
                            hideOverlap: true,
                            moveOverlap: 'shiftY'
                        }
                    },
                    {
                        name: prevYear.toString(),
                        type: "line",
                        smooth: true,
                        data: Array(12).fill(0),
                        label: {
                            show: true,
                            position: "bottom",
                            formatter: (params) => this.formatNumber(params.value),
                            distance: 10,
                            avoidLabelOverlap: true,
                            color: "#333"
                        },
                        labelLayout: {
                            hideOverlap: true,
                            moveOverlap: 'shiftY'
                        }
                    }
                ]
            };

            this.barChart.setOption(this.chartOptions.bar, true);
            this.lineChart.setOption(this.chartOptions.line, true);
        },

        updateCharts() {
            if (!this.barChart || !this.lineChart || !this.chartOptions.bar || !this.chartOptions.line) return;

            const currYear = this.year.getFullYear();
            const prevYear = currYear - 1;

            const currData = Array(12).fill(0);
            const prevData = Array(12).fill(0);
            const currAmount = Array(12).fill(0);
            const prevAmount = Array(12).fill(0);

            this.currentYearData.forEach((item) => {
                const idx = item.mm - 1;
                if (idx >= 0 && idx < 12) {
                    currData[idx] = item.totalReceiveQuantity;
                    currAmount[idx] = item.preSaleValue;
                }
            });

            this.lastYearData.forEach((item) => {
                const idx = item.mm - 1;
                if (idx >= 0 && idx < 12) {
                    prevData[idx] = item.totalReceiveQuantity;
                    prevAmount[idx] = item.preSaleValue;
                }
            });

            this.chartOptions.bar.series[0].data = currData;
            this.chartOptions.bar.series[1].data = prevData;
            this.chartOptions.bar.title.text = `${currYear} 与 ${prevYear} 实际发货量对比`;
            this.chartOptions.bar.series[0].name = currYear.toString();
            this.chartOptions.bar.series[1].name = prevYear.toString();

            this.chartOptions.line.series[0].data = currAmount;
            this.chartOptions.line.series[1].data = prevAmount;
            this.chartOptions.line.title.text = `${currYear} 与 ${prevYear} 有效金额对比`;
            this.chartOptions.line.series[0].name = currYear.toString();
            this.chartOptions.line.series[1].name = prevYear.toString();

            requestAnimationFrame(() => {
                this.barChart.resize();
                this.lineChart.resize();
                this.barChart.setOption(this.chartOptions.bar, { notMerge: false });
                this.lineChart.setOption(this.chartOptions.line, { notMerge: false });
            });
        },

        async fetchData() {
            const cacheKey = JSON.stringify({
                year: this.year.getFullYear(),
                groupBy: this.groupBy,
                managerUuid: this.managerUuid,
                seedFarmUuid: this.seedFarmUuid,
                stationUuid: this.distributionStationUuid
            });

            if (this.dataCache.has(cacheKey)) {
                const cachedData = this.dataCache.get(cacheKey);
                this.currentYearData = cachedData.currentYearData;
                this.lastYearData = cachedData.lastYearData;
                this.dataLoaded = true;
                this.updateCharts();
                return;
            }

            this.loading = true;
            try {
                const res = await getSalesStatistical({
                    year: this.year.getFullYear(),
                    groupBy: this.groupBy,
                    managerUuid: this.managerUuid,
                    seedFarmUuid: this.seedFarmUuid,
                    stationUuid: this.distributionStationUuid
                });

                const code = res && res.code;
                const list = res.data.list;
                if (code === 0 && Array.isArray(list)) {
                    const currYear = this.year.getFullYear();
                    const prevYear = currYear - 1;

                    const currentYearData = list.filter(
                        (item) => item.yyyy === currYear
                    );
                    const lastYearData = list.filter(
                        (item) => item.yyyy === prevYear
                    );

                    this.currentYearData = currentYearData;
                    this.lastYearData = lastYearData;

                    this.dataCache.set(cacheKey, {
                        currentYearData,
                        lastYearData
                    });

                    this.dataLoaded = true;
                    this.updateCharts();
                }
            }
            catch (e) {
                console.error("获取统计数据失败", e);
                this.dataLoaded = false;
            }
            finally {
                this.loading = false;
            }
        },

        chooseGroup(group) {
            switch (group) {
                case "all":
                    this.groupBy = "all";
                    this.managerListShow = false;
                    this.seedFarmListShow = false;
                    this.distributionStationListShow = false;
                    this.fetchData();
                    break;
                case "seedFarm":
                    this.groupBy = "seedFarm";
                    this.seedFarmListShow = true;
                    this.managerListShow = false;
                    this.distributionStationListShow = false;
                    break;
                case "manager":
                    this.groupBy = "manager";
                    this.managerListShow = true;
                    this.seedFarmListShow = false;
                    this.distributionStationListShow = false;
                    break;
                case "distributionStation":
                    this.groupBy = "distributionStation";
                    this.managerListShow = false;
                    this.seedFarmListShow = false;
                    this.distributionStationListShow = true;
                    break;
            }
        },
        getCustomerManagerUserTableRecords() {
            let tableSearch = search.getSearchParams({
                closeAllConditions: true
            });
            let self = this;
            getCustomerManagerUserList(tableSearch)
                .then(function (res) {
                    if (res.code === 0) {
                        self.managerList
                            = res.data && res.data.records
                                ? res.data.records
                                : [];
                    }
                    else {
                        self.managerList = [];
                    }
                })
                .catch(function (error) {
                    self.managerList = [];
                });
        },
        getSeedFarmList() {
            let tableSearch = search.getSearchParams({
                closeAllConditions: true
            });
            let self = this;
            getSeedFarmList(tableSearch)
                .then(function (res) {
                    if (res.code === 0) {
                        self.seedFarmList
                            = res.data && res.data.records
                                ? res.data.records
                                : [];
                    }
                    else {
                        self.seedFarmList = [];
                    }
                })
                .catch(function (error) {
                    self.seedFarmList = [];
                });
        },
        getDistributionStationList() {
            let tableSearch = search.getSearchParams({
                closeAllConditions: true
            });
            let self = this;
            getDistributionStationList(tableSearch).then(res => {
                if (res.code === 0) {
                    self.distributionStationList
                        = res.data && res.data.records
                            ? res.data.records
                            : [];
                }
                else {
                    self.distributionStationList = [];
                }
            });
        },
        handleSearch: debounce(function () {
            this.fetchData();
        }, 300)
    }
};
</script>

<style scoped>
.sales-stat-card {
    --filter-gap: 16px;
    padding: 16px;
    background-color: #fefefe;
    will-change: transform;
    transform: translateZ(0);
}
.filter-bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--filter-gap);
    margin-bottom: 20px;
    background-color: #fafafa;
    padding: 12px 16px;
    border-radius: 8px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    will-change: transform;
    transform: translateZ(0);
}
.filter-bar .title {
    font-size: 18px;
    font-weight: 600;
    margin-right: auto;
    color: #2c3e50;
}
.filter-bar .filter-item {
    display: flex;
    align-items: center;
    font-size: 13px;
    color: #555;
    gap: 6px;
}
.filter-bar .label {
    white-space: nowrap;
    font-weight: 500;
}
.chart-wrapper {
    margin-top: 24px;
    padding: 12px;
    background-color: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.03);
    will-change: transform;
    transform: translateZ(0);
    contain: layout style paint;
}

.chart-wrapper > div {
    width: 100%;
    height: 400px;
    will-change: transform;
    transform: translateZ(0);
    box-sizing: border-box;
    display: block;
}

.search-btn {
    will-change: transform;
    transform: translateZ(0);
    transition: all 0.2s ease;
}

.search-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.search-btn:active {
    transform: translateY(0);
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

.spin-icon-load {
    animation: ani-demo-spin 1s linear infinite;
}

@keyframes ani-demo-spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}
</style>
